"use strict";

const observer = new (window.MutationObserver || window.WebKitMutationObserver || window.MozMutationObserver)(
    (mutations) => {
        mutations.forEach((mutation) => {
            Array.from(mutation.addedNodes)
                .filter(node => node.nodeType === 1) // Ensure it's an Element
                .forEach(node => observer.execute(node));
        });
    }
);

observer.listeners = {};

/**
 * Adds a listener for a selector.
 *
 * @param {string} selector - The CSS selector.
 * @param {function} callback - The callback function.
 * @param {string|number|null} [key=null] - The optional key for the callback.
 */
observer.add = function (selector, callback, key = null) {

    // Validation
    if (typeof selector !== 'string') {
        console.warn('selector must be a string');
        return;
    }
    if (typeof callback !== 'function') {
        console.warn('callback must be a function');
        return;
    }

    if (!this.listeners[selector]) {
        this.listeners[selector] = {};
        // Initialize numeric index for numeric keys
        this.listeners[selector].__index__ = 0;
    }

    if (key === null) {
        // Generate an integer key that hasn't been used
        key = this.listeners[selector].__index__++;
    }

    this.listeners[selector][key] = callback;

    // Execute the callback for existing elements matching the selector
    document.body.querySelectorAll(selector).forEach((element) => {
        try {
            callback.call(element, element);
        } catch (error) {
            console.error('Error executing callback:', error);
        }
    });
};

/**
 * Retrieves listeners based on the provided selector and key.
 *
 * @param {string} [selector] - The CSS selector.
 * @param {string|number} [key] - The key for the callback.
 * @returns {object|function|null} - Returns the callbacks or null if not found.
 */
observer.get = function (selector, key) {
    if (selector === undefined) {
        return this.listeners;
    }
    if (!this.listeners.hasOwnProperty(selector)) {
        return undefined;
    }
    if (key === undefined) {
        return this.listeners[selector];
    }
    if (!this.listeners[selector].hasOwnProperty(key)) {
        return undefined;
    }
    return this.listeners[selector][key];
};

/**
 * Removes listeners based on the provided selector and key. selectors must be exact matches, not what matches the query selector.
 * For example, removing 'div' will not remove listeners for 'div.class' or 'div#id'.
 *
 * @param {string} selector - The CSS selector.
 * @param {string|number} [key] - The key for the callback.
 */
observer.remove = function (selector, key) {
    if (selector === undefined) {
        // Remove all listeners
        this.listeners = {};
        return;
    } else if (typeof selector !== 'string') {
        console.warn('Selector must be a string');
        return;
    }

    if (!this.listeners.hasOwnProperty(selector)) {
        // No listeners for this selector
        return;
    }

    if (key === undefined) {
        // Remove all callbacks for this selector
        delete this.listeners[selector];
    } else {
        if (this.listeners[selector].hasOwnProperty(key)) {
            delete this.listeners[selector][key];
            // Clean up if no more callbacks under this selector
            const keys = Object.keys(this.listeners[selector]);
            if (keys.length === 1 && keys.includes('__index__')) {
                delete this.listeners[selector];
            }
        }
    }
};

observer.execute = function (node) {
    for (const [selector, callbacks] of Object.entries(this.listeners)) {
        const elements = [];

        if (node.nodeType === 1 && node.matches(selector)) {
            elements.push(node);
        }

        if (node.querySelectorAll) {
            elements.push(...node.querySelectorAll(selector));
        }

        elements.forEach((element) => {
            for (const [key, callback] of Object.entries(callbacks)) {
                if (key !== '__index__') { // Skip the key index property
                    try {
                        callback.call(element, element);
                    } catch (error) {
                        console.error('Error executing callback:', error);
                    }
                }
            }
        });
    }
};

observer.observe(document.body, {
    childList: true,
    subtree: true,
    characterData: false,
    attributes: false
});

export default observer;

/*
window.watch = (selector, callback, key) => {
    switch (typeof callback) {
        case 'function':
            observer.add(selector, callback, key);
            break;
        case 'string':
            if (observer[callback]) {
                return observer[callback](selector, key);
            } else {
                return observer.remove(selector, callback);
            }
    }
    return document.querySelectorAll(selector);
};
*/
